import { memo, useContext, useMemo } from 'react'
import { Handle, Position, type NodeProps } from 'reactflow'
import { GitBranch } from 'lucide-react'
import { ExecutionContext } from '../../../components/flow-nodes/shared/ExecutionContext'
import type { Condition } from './types'

// Handle 样式常量
const HANDLE_STYLE_AMBER = {
  width: 12,
  height: 12,
  background: '#f59e0b',
  border: '2px solid #d97706',
} as const

// 自定义节点组件：条件/路由节点
export const RouterNode = memo(({ data, selected, id }: NodeProps) => {
  const { activeNodeId, visitedNodeIds } = useContext(ExecutionContext)
  const isActive = activeNodeId === id
  const isVisited = visitedNodeIds.has(id)
  
  const conditions: Condition[] = data.conditions || []
  const handleCount = conditions.length
  
  // 预计算 handle 位置和标签
  // 注意：Handle.id 必须等于 Condition.id，这样 Edge.sourceHandle 才能正确匹配后端的路由逻辑
  const handles = useMemo(() => {
    return conditions.map((condition, index) => ({
      id: condition.id,  // 使用稳定的 condition ID，而非索引
      top: `${((index + 1) / (conditions.length + 1)) * 100}%`,
      name: condition.name || `条件 ${index + 1}`
    }))
  }, [conditions])
  
  return (
    <div className={`px-4 py-3 shadow-lg rounded-lg bg-amber-50 border-2 min-w-[180px] transition-all duration-300 ${
      isActive 
        ? 'border-amber-500 ring-4 ring-amber-400/50 shadow-xl scale-105 animate-pulse' 
        : selected 
          ? 'border-amber-500 ring-2 ring-amber-500/20 shadow-xl' 
          : isVisited 
            ? 'border-amber-400 ring-2 ring-amber-200/50' 
            : 'border-amber-300'
    }`}>
      <Handle
        type="target"
        position={Position.Left}
        style={HANDLE_STYLE_AMBER}
      />
      
      <div className="flex items-center gap-2 mb-2">
        <div className={`p-1.5 rounded-md transition-colors duration-300 ${isActive ? 'bg-amber-500' : 'bg-amber-100'}`}>
          <GitBranch className={`h-4 w-4 ${isActive ? 'text-white' : 'text-amber-600'}`} />
        </div>
        <div>
          <div className="font-medium text-sm text-amber-900">{data.label}</div>
          <div className="text-xs text-amber-600">{handleCount} 个分支</div>
        </div>
      </div>
      
      {/* 条件名称列表 */}
      <div className="space-y-1 border-t border-amber-200 pt-2">
        {handles.map((handle) => (
          <div key={handle.id} className="flex items-center justify-end gap-1 text-[10px] text-amber-700">
            <span className="truncate max-w-[100px]">{handle.name}</span>
            <div className="w-2 h-2 rounded-full bg-amber-400" />
          </div>
        ))}
      </div>
      
      {handles.map((handle) => (
        <Handle
          key={handle.id}
          type="source"
          position={Position.Right}
          id={handle.id}
          style={{ 
            ...HANDLE_STYLE_AMBER, 
            top: handle.top 
          }}
        />
      ))}
    </div>
  )
})

RouterNode.displayName = 'RouterNode'